import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  Box,
  Chip,
  Paper,
  CircularProgress,
  Grid,
  Icon
} from '@mui/material';
import { useApiCall } from '../utils/hooks';
import { pdfApi } from '../utils/api';

function DownloadPDFs() {
  const [result, setResult] = useState(null);
  const [productName, setProductName] = useState('Example Product');
  const [pdfUrls, setPdfUrls] = useState('');
  const { loading, execute } = useApiCall();

  const handleDownloadPDFs = async () => {
    try {
      const urlsArray = pdfUrls.split('\n').filter(url => url.trim()).map(url => url.trim());
      const data = await execute(() => pdfApi.downloadPDFs({
        productName,
        pdfUrls: urlsArray
      }));
      
      setResult({
        success: true,
        status: 'Success',
        data: data
      });
    } catch (error) {
      setResult({
        error: error.message,
        success: false
      });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Download PDFs Service
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Manually download and upload PDFs to Google Cloud Storage for processing.
          </Typography>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            API Documentation
          </Typography>
          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Chip label="POST" color="warning" size="small" sx={{ mr: 1 }} />
            <Typography component="span" sx={{ fontFamily: 'monospace' }}>
              /api/download-pdfs
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            Downloads PDFs from provided URLs and uploads them to Google Cloud Storage.
          </Typography>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            PDF Download Tool
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Product Name"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                placeholder="Enter product name"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="PDF URLs (one per line)"
                multiline
                rows={6}
                value={pdfUrls}
                onChange={(e) => setPdfUrls(e.target.value)}
                placeholder="https://example.com/document1.pdf&#10;https://example.com/document2.pdf"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Button
                variant="contained"
                onClick={handleDownloadPDFs}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <Icon>download</Icon>}
              >
                {loading ? 'Downloading...' : 'Download PDFs'}
              </Button>
            </Grid>
          </Grid>

          {result && (
            <Box sx={{ mt: 3 }}>
              {result.error ? (
                <Alert severity="error">
                  Error: {result.error}
                </Alert>
              ) : (
                <>
                  <Typography variant="body2" gutterBottom>
                    Status: <Chip 
                      label={result.status} 
                      color={result.success ? 'success' : 'error'} 
                      size="small" 
                    />
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50', mt: 1 }}>
                    <pre style={{ 
                      margin: 0, 
                      fontFamily: 'monospace', 
                      fontSize: '0.875rem',
                      whiteSpace: 'pre-wrap'
                    }}>
                      {JSON.stringify(result.data, null, 2)}
                    </pre>
                  </Paper>
                </>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default DownloadPDFs;
